import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ytId } from "@/pages/YouTubePage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const field =
  "w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60";
const empty = { title: "", url: "", tags: "", visible: true };

export const YoutubeManager = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/youtube").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...empty, ...v }); setEditId(v.id); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { title: form.title, url: form.url, tags: form.tags, visible: form.visible };
      if (editId) await api.put(`/admin/youtube/${editId}`, payload);
      else await api.post("/admin/youtube", payload);
      toast.success(editId ? "Vidéo mise à jour" : "Vidéo ajoutée");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette vidéo ?")) return;
    await api.delete(`/admin/youtube/${id}`);
    toast.success("Vidéo supprimée");
    load();
  };

  const toggleVisible = async (v) => {
    await api.put(`/admin/youtube/${v.id}`, { ...v, visible: !v.visible });
    load();
  };

  return (
    <div data-testid="admin-youtube-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-amber-50">Vidéos YouTube</h1>
          <p className="text-slate-400">Vidéos affichées sur la page YouTube du site.</p>
        </div>
        <button onClick={openNew} data-testid="admin-add-youtube-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform">
          <Plus size={18} /> Ajouter
        </button>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((v) => {
          const id = ytId(v.url);
          return (
            <div key={v.id} className="glass rounded-2xl overflow-hidden" data-testid={`youtube-row-${v.id}`}>
              <div className="aspect-video bg-black">
                {id && <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="font-serif text-lg text-amber-100 truncate">{v.title}</h3>
                  {!v.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 shrink-0">Masqué</span>}
                </div>
                {v.tags && <span className="text-xs font-mono-accent text-amber-400/70">{v.tags}</span>}
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => toggleVisible(v)} data-testid={`youtube-toggle-${v.id}`} className="p-2 rounded-lg hover:bg-white/5 text-slate-400">{v.visible ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                  <button onClick={() => openEdit(v)} data-testid={`youtube-edit-${v.id}`} className="p-2 rounded-lg hover:bg-white/5 text-amber-300"><Pencil size={15} /></button>
                  <button onClick={() => remove(v.id)} data-testid={`youtube-delete-${v.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0D0D12] border-amber-500/20 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-amber-100">{editId ? "Modifier la vidéo" : "Nouvelle vidéo"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input className={field} placeholder="Titre" data-testid="youtube-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input className={field} placeholder="URL YouTube (https://youtu.be/...)" data-testid="youtube-form-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <input className={field} placeholder="Étiquettes (ex: Méditation)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="accent-amber-500" /> Visible sur le site</label>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving || !form.title || !form.url} data-testid="youtube-form-save" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Enregistrer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
